import { type UserDisplayItem } from "../../user/user-display-utils";
import { UserDisplayHelper } from "../../user/user-display-utils";
import { WysiwygListener } from "../wysiwyg-listener";
import { WysiwygMentionRender } from "./wysiwyg-mention-render";
import { MentionControllerBase, type MentionMatch } from "./wysiwyg-mention-handler";

interface QuillSelectionLike {
    index: number;
    length: number;
}

interface QuillBoundsLike {
    left: number;
    top: number;
    height: number;
}

interface QuillClipboardLike {
    dangerouslyPasteHTML(index: number, html: string, source?: string): void;
}

interface QuillMentionLike {
    root: HTMLElement;
    getSelection(focus?: boolean): QuillSelectionLike | null;
    getText(index: number, length: number): string;
    getBounds(index: number, length?: number): QuillBoundsLike;
    deleteText(index: number, length: number, source?: string): void;
    setSelection(index: number, length: number, source?: string): void;
    clipboard: QuillClipboardLike;
}

class WysiwygMentionController extends MentionControllerBase {
    public constructor(
        private readonly textarea: HTMLTextAreaElement,
        private readonly quill: QuillMentionLike,
        private readonly editorDiv: HTMLElement,
    ) {
        super(editorDiv);
    }

    public override bind(): void {
        WysiwygListener.bindMentionInteractions(this.quill.root, this.dropdownEl, this);
    }

    protected override resolveMentionMatch(): MentionMatch | null {
        const selection = this.quill.getSelection(true);
        if (!selection) {
            return null;
        }

        const cursorIndex = Number(selection.index ?? 0);
        const contextStart = Math.max(0, cursorIndex - 64);
        const contextText = this.quill.getText(contextStart, cursorIndex - contextStart);
        const match = contextText.match(/(?:^|\s)@([\p{L}\p{N}_\-.]{1,30})$/u);
        if (!match) {
            return null;
        }

        const query = String(match[1] || "").trim();
        const startIndex = cursorIndex - query.length - 1;
        if (startIndex < 0) {
            return null;
        }

        return { query, startIndex, cursorIndex };
    }

    protected override doInsertMention(user: UserDisplayItem, match: MentionMatch): void {
        const replaceLength = match.cursorIndex - match.startIndex;
        this.quill.deleteText(match.startIndex, replaceLength, "user");

        const html = `${UserDisplayHelper.buildMentionAnchorHtml(user)} `;
        this.quill.clipboard.dangerouslyPasteHTML(match.startIndex, html, "user");

        const nextCursor = match.startIndex + UserDisplayHelper.ensureAtPrefix(user.displayName).length + 1;
        this.quill.setSelection(nextCursor, 0, "user");
        this.textarea.dispatchEvent(new Event("input", { bubbles: true }));
    }

    protected override doPositionDropdown(match: MentionMatch): void {
        try {
            const bounds = this.quill.getBounds(match.cursorIndex);
            WysiwygMentionRender.positionDropdown(this.dropdownEl, bounds);
        } catch (_error) {
            WysiwygMentionRender.positionDropdownFallback(this.dropdownEl);
        }
    }

    protected override isOwnEditorEl(target: Node): boolean {
        return this.editorDiv.contains(target);
    }

    protected override onActiveIndexChanged(): void {
        this.syncActiveDescendant();
    }

    private syncActiveDescendant(): void {
        const activeOptionId = WysiwygMentionRender.getActiveOptionId(this.dropdownEl, this.activeIndex);

        // `activeIndex` is private in the base class; access via the render helper result
        if (activeOptionId === "") {
            this.quill.root.removeAttribute("aria-activedescendant");
            this.quill.root.removeAttribute("aria-controls");
            this.quill.root.removeAttribute("aria-expanded");
            return;
        }

        this.quill.root.setAttribute("aria-controls", WysiwygMentionRender.getDropdownId(this.dropdownEl));
        this.quill.root.setAttribute("aria-expanded", "true");
        this.quill.root.setAttribute("aria-activedescendant", activeOptionId);
    }
}

export class WysiwygMentionSetup {
    public static init(
        textarea: HTMLTextAreaElement | null,
        quill: QuillMentionLike | null,
        editorDiv: HTMLElement | null,
    ): void {
        if (!(textarea instanceof HTMLTextAreaElement) || !(editorDiv instanceof HTMLElement) || !quill) {
            return;
        }

        if (editorDiv.dataset.wysiwygMentionInitialized === "1") {
            return;
        }

        const controller = new WysiwygMentionController(textarea, quill, editorDiv);
        controller.bind();
        editorDiv.dataset.wysiwygMentionInitialized = "1";
    }
}
